import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  useCandidatesWithApplicationsQuery,
  useEvaluationsQuery,
  useJobQuery,
} from "../../graphql/generated/graphql";
import NavBar from "../../components/NavBar/NavBar";
import CandidateDetails from "../../components/CandidatesList/CandidateDetails";
import CandidatesSkeleton from "../../components/Skeletons/CandidatesSkeleton";
import EmptyState from "../../components/Empty/EmptyState";
import Modal from "../../components/Modal/Modal";
import { safeParseInt } from "../../utils";
import JobDetails from "../../components/JobDetails/JobDetails";
import { useAverageEvaluations } from "../../hooks/useAverageEvaluations";
import { useFilteredCandidates } from "../../hooks/useFilteredCandidates";
import person from "../../image/placeholder.png";
import { usePipelineStageFilteredCandidates } from "../../hooks/usePipelineStageFilteredCandidates";

export default function CandidatesList() {
  const location = useLocation();
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  const queryParams = new URLSearchParams(location.search);
  const jobId = queryParams.get("jobId");
  const safeJobId = safeParseInt(jobId!);

  const { data: jobData } = useJobQuery({
    variables: { id: safeJobId },
    skip: !jobId,
  });

  const { data: evaluationsData, loading: evaluationsLoading } =
    useEvaluationsQuery();
  const { data: candidatesData, loading: candidatesLoading } =
    useCandidatesWithApplicationsQuery({
      variables: {},
      skip: !jobId,
    });

  const jobTitle = jobData?.job?.translations.nodes[0]?.title || "loading...";

  const averageEvaluations = useAverageEvaluations(evaluationsData);
  const filteredCandidates = useFilteredCandidates(candidatesData, safeJobId);
  const pipelineStageFilteredCandidates =
    usePipelineStageFilteredCandidates(filteredCandidates);

  const openModal = (jobId: number) => {
    setSelectedJobId(jobId);
    setModalOpen(true);
  };

  if (candidatesLoading) {
    return <CandidatesSkeleton />;
  }

  const candidatesToDisplay = pipelineStageFilteredCandidates;

  return (
    <main className="md:h-screen">
      <NavBar name={jobTitle} />
      <div className="w-full border-b">
        <div className="flex justify-center">
          <button
            className="w-2/3 md:w-1/3 flex justify-center items-center gap-x-2 mx-auto my-2 py-1.5 text-center border rounded-lg bg-gray-100 hover:bg-opacity-80 shadow-sm hover:shadow"
            onClick={() => openModal(safeJobId)}
          >
            نمایش اطلاعات آگهی
          </button>
        </div>
      </div>
      <div className="sm:h-11/12 md:p-4 p-2 md:h-[85%] md:overflow-auto max-sm:w-screen max-w-xl mx-auto sm:border sm:mt-8 sm:rounded-lg sm:items-center sm:shadow-lg">
        {candidatesToDisplay.length === 0 ? (
          <EmptyState
            title={"هنوز رزومه ای دریافت نشده است."}
            subtitle={"هیچ کارجویی برای این موقعیت شغلی درخواست ثبت نکرده است."}
          />
        ) : (
          candidatesToDisplay.map((candidate) => (
            <Link
              key={candidate.id}
              to={`/candidate/${candidate.id}?jobId=${jobId}?jobTitle=${jobTitle}`}
            >
              <CandidateDetails
                id={candidate.id}
                name={candidate.translations.nodes[0]?.name || "Unknown"}
                job={jobTitle}
                photo={candidate.photoUrl || person}
                averageEvaluation={averageEvaluations[candidate.nodeId] || 0}
                isHired={candidate.isHired}
                loading={evaluationsLoading}
              />
            </Link>
          ))
        )}
      </div>
      {isModalOpen && selectedJobId !== null && (
        <Modal open={isModalOpen} setOpen={setModalOpen} request={undefined}>
          <JobDetails jobId={selectedJobId} />
        </Modal>
      )}
    </main>
  );
}
